import React, { useState } from 'react';
import { useData } from '../../contexts/DataContext';
import { useTranslation } from 'react-i18next';
import { Artist, PayoutInfo, PayPalInfo } from '../../types';
import { Toast, useToast } from '../../components/shared';

const AdminEarnings: React.FC = () => {
  const { earnings, artists, releases, sendNotificationToUser, addManualEarning, getPayPalInfoByUserId } = useData();
  const [selectedMonth, setSelectedMonth] = useState('2024-02');
  const [selectedArtist, setSelectedArtist] = useState<Artist | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'payments'>('overview');
  const [showAddEarningModal, setShowAddEarningModal] = useState(false);
  const { t } = useTranslation();
  const { toasts, hideToast, showSuccess, showError } = useToast();

  const monthlyEarnings = earnings.filter(e => e.month === selectedMonth);
  
  const totalRevenue = monthlyEarnings.reduce((total, earning) => {
    return total + parseFloat(earning.revenue);
  }, 0);

  const platformBreakdown = monthlyEarnings.reduce((acc, earning) => {
    if (!acc[earning.platform]) {
      acc[earning.platform] = 0;
    }
    acc[earning.platform] += parseFloat(earning.revenue);
    return acc;
  }, {} as Record<string, number>);

  const artistBreakdown = monthlyEarnings.reduce((acc, earning) => {
    const artist = artists.find(a => a.user_id === earning.user_id);
    const artistName = artist?.display_name || t('common.unknown');
    
    if (!acc[artistName]) {
      acc[artistName] = {
        artist: artist,
        revenue: 0,
        streams: 0,
        tracks: new Set()
      };
    }
    acc[artistName].revenue += parseFloat(earning.revenue);
    acc[artistName].streams += earning.streams;
    acc[artistName].tracks.add(earning.track_name);
    return acc;
  }, {} as Record<string, { artist: Artist | undefined, revenue: number, streams: number, tracks: Set<string> }>);

  const pendingPayments = monthlyEarnings.filter(e => e.payment_status === 'pending');
  const paidPayments = monthlyEarnings.filter(e => e.payment_status === 'paid');

  const availableMonths = ['2024-01', '2024-02', '2024-03'];

  // Mock payout info - gerçek uygulamada API'den gelecek
  const mockPayoutInfo: Record<string, PayoutInfo> = {
    'artist_1': {
      user_id: 'artist_1',
      type: 'bank',
      iban: 'TR320010009999901234567890',
      full_name: 'Ahmet Yılmaz',
      address: 'Kadıköy, İstanbul',
      city: 'İstanbul',
      postal_code: '34000',
      country: 'Türkiye',
      currency: 'TRY'
    },
    'artist_2': {
      user_id: 'artist_2',
      type: 'paypal',
      paypal_email: 'artist@example.com',
      full_name: 'Ayşe Demir',
      address: 'Çankaya, Ankara',
      city: 'Ankara',
      postal_code: '06000',
      country: 'Türkiye',
      currency: 'TRY'
    }
  };

  const getPlatformColor = (platform: string) => {
    const colors = {
      'Spotify': 'bg-green-500',
      'Apple Music': 'bg-gray-500',
      'YouTube Music': 'bg-red-500',
      'Amazon Music': 'bg-blue-500',
      'Deezer': 'bg-purple-500',
      'Tidal': 'bg-indigo-500'
    };
    return colors[platform as keyof typeof colors] || 'bg-orange-500';
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30';
      case 'paid':
        return 'bg-green-500/20 text-green-300 border-green-500/30';
      case 'processing':
        return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
      default:
        return 'bg-gray-500/20 text-gray-300 border-gray-500/30';
    }
  };

  const getPaymentStatusText = (status: string) => {
    switch (status) {
      case 'pending':
        return t('payment.pending');
      case 'paid':
        return t('payment.paid');
      case 'processing':
        return t('payment.processing');
      default:
        return status;
    }
  };

  const handlePayment = async (artistId: string, amount: number) => {
    try {
      // Gerçek uygulamada API çağrısı yapılacak
      
      // Mock payment processing
      setTimeout(async () => {
        showSuccess(`₺${amount} ${t('payment.paymentSuccessful')}`);
        
        // Artist'e bildirim gönder
        await sendNotificationToUser(
          artistId,
          'payment_sent',
          'Ödeme Gönderildi!',
          `₺${amount.toFixed(2)} tutarında ödemeniz gönderildi.`,
          '/earnings',
          {
            payment_amount: amount,
            payment_date: new Date().toISOString()
          }
        );
        
        // Burada earnings'teki payment_status güncellenmeli
      }, 1000);
    } catch (error) {
      console.error('Payment error:', error);
      showError('Ödeme işlemi sırasında hata oluştu.');
    }
  };

  const AddEarningModal = ({ onClose }: { onClose: () => void }) => {
    const [formData, setFormData] = useState({
      artist_id: '',
      platforms: [] as string[],
      track_ids: [] as string[],
      amount: '',
      currency: 'TRY',
      payment_date: new Date().toISOString().split('T')[0],
      notes: ''
    });

    const availablePlatforms = [
      'Spotify', 'Apple Music', 'YouTube Music', 'Amazon Music',
      'Deezer', 'Tidal', 'SoundCloud', 'Bandcamp'
    ];

    const selectedArtist = artists.find(a => a.id === formData.artist_id);
    // Get all releases of the artist that are published or approved
    const artistReleases = releases.filter(r => 
      r.artist_id === formData.artist_id && 
      (r.status === 'published' || r.status === 'approved')
    );
    // Get all tracks from published/approved releases
    const availableTracks = artistReleases.flatMap(release => 
      release.tracks.map(track => ({ 
        ...track, 
        release_title: release.title,
        release_status: release.status,
        release_date: release.release_date
      }))
    );

    const handlePlatformToggle = (platform: string) => {
      setFormData(prev => ({
        ...prev,
        platforms: prev.platforms.includes(platform)
          ? prev.platforms.filter(p => p !== platform)
          : [...prev.platforms, platform]
      }));
    };

    const handleTrackToggle = (trackId: string) => {
      setFormData(prev => ({
        ...prev,
        track_ids: prev.track_ids.includes(trackId)
          ? prev.track_ids.filter(id => id !== trackId)
          : [...prev.track_ids, trackId]
      }));
    };

    const handleSelectAllTracks = () => {
      setFormData(prev => ({
        ...prev,
        track_ids: availableTracks.map(track => track.id)
      }));
    };

    const handleClearAllTracks = () => {
      setFormData(prev => ({
        ...prev,
        track_ids: []
      }));
    };

    const handleSubmit = async () => {
      if (!formData.artist_id || !formData.amount || formData.platforms.length === 0) {
        showError('Lütfen tüm zorunlu alanları doldurun.');
        return;
      }

      try {
        const success = await addManualEarning({
          artist_id: formData.artist_id,
          platforms: formData.platforms,
          track_ids: formData.track_ids,
          amount: parseFloat(formData.amount),
          currency: formData.currency,
          month: selectedMonth,
          payment_date: formData.payment_date,
          notes: formData.notes
        });

        if (success) {
          showSuccess('Gelir başarıyla eklendi!');
          
          // Send notification to artist
          if (selectedArtist?.user_id) {
            await sendNotificationToUser(
              selectedArtist.user_id,
              'payment_sent',
              'Yeni Gelir Eklendi!',
              `${formData.currency} ${formData.amount} tutarında yeni gelir hesabınıza eklendi.`,
              '/earnings',
              {
                payment_amount: parseFloat(formData.amount),
                currency: formData.currency,
                platforms: formData.platforms
              }
            );
          }

          onClose();
        } else {
          showError('Gelir eklenirken hata oluştu.');
        }
      } catch (error) {
        console.error('Add earning error:', error);
        showError('Gelir eklenirken hata oluştu.');
      }
    };

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-gray-900 rounded-xl p-6 max-w-4xl w-full mx-4 border border-white/20 max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-white">Manuel Gelir Ekle</h2>
            <button
              onClick={onClose}
              className="text-white/60 hover:text-white"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-6">
            {/* Artist Selection */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Sanatçı Seçin *
              </label>
              <select
                className="input-field"
                value={formData.artist_id}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  artist_id: e.target.value,
                  track_ids: [] // Reset tracks when artist changes
                }))}
                required
              >
                <option value="">Sanatçı Seçin</option>
                {artists.filter(a => a.status === 'active').map((artist) => (
                  <option key={artist.id} value={artist.id}>
                    {artist.display_name} ({artist.first_name} {artist.last_name})
                  </option>
                ))}
              </select>
            </div>

            {/* Platform Selection */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Platform(lar) Seçin * (Birden fazla seçilebilir)
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {availablePlatforms.map((platform) => (
                  <label key={platform} className="flex items-center p-3 bg-white/5 rounded-lg border border-white/10 cursor-pointer hover:bg-white/10 transition-colors">
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-orange-500 focus:ring-orange-500 border-white/30 rounded bg-white/10"
                      checked={formData.platforms.includes(platform)}
                      onChange={() => handlePlatformToggle(platform)}
                    />
                    <span className="ml-3 text-sm text-white">{platform}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Track Selection */}
            {selectedArtist && availableTracks.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Şarkı(lar) Seçin (İsteğe Bağlı - Birden fazla seçilebilir)
                  {formData.track_ids.length > 0 && (
                    <span className="ml-2 text-xs bg-orange-500/20 text-orange-300 px-2 py-1 rounded-full">
                      {formData.track_ids.length} şarkı seçildi
                    </span>
                  )}
                </label>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs text-white/60">
                    Sadece yayınlanmış ve onaylanmış şarkılar gösterilmektedir
                  </p>
                  {availableTracks.length > 1 && (
                    <div className="flex space-x-2">
                      <button
                        type="button"
                        onClick={handleSelectAllTracks}
                        className="text-xs bg-blue-500/20 text-blue-300 px-2 py-1 rounded hover:bg-blue-500/30 transition-colors"
                      >
                        Tümünü Seç
                      </button>
                      <button
                        type="button"
                        onClick={handleClearAllTracks}
                        className="text-xs bg-red-500/20 text-red-300 px-2 py-1 rounded hover:bg-red-500/30 transition-colors"
                      >
                        Temizle
                      </button>
                    </div>
                  )}
                </div>
                <div className="max-h-48 overflow-y-auto border border-white/20 rounded-lg p-2">
                  {availableTracks.map((track) => (
                    <label key={track.id} className="flex items-center p-3 hover:bg-white/5 rounded cursor-pointer border-b border-white/5 last:border-b-0">
                      <input
                        type="checkbox"
                        className="h-4 w-4 text-orange-500 focus:ring-orange-500 border-white/30 rounded bg-white/10"
                        checked={formData.track_ids.includes(track.id)}
                        onChange={() => handleTrackToggle(track.id)}
                      />
                      <div className="ml-3 flex-1">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-white">{track.title}</span>
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            track.release_status === 'published' 
                              ? 'bg-green-500/20 text-green-300 border border-green-500/30'
                              : 'bg-blue-500/20 text-blue-300 border border-blue-500/30'
                          }`}>
                            {track.release_status === 'published' ? 'Yayınlandı' : 'Onaylandı'}
                          </span>
                        </div>
                        <p className="text-xs text-white/60 mt-1">{track.release_title}</p>
                        <p className="text-xs text-white/40 mt-1">
                          Yayın Tarihi: {new Date(track.release_date).toLocaleDateString('tr-TR')}
                        </p>
                      </div>
                    </label>
                  ))}
                </div>
                {availableTracks.length === 0 && (
                  <div className="text-center py-6 border border-white/20 rounded-lg">
                    <svg className="w-8 h-8 text-white/40 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2z" />
                    </svg>
                    <p className="text-sm text-white/60">Bu sanatçının yayınlanmış şarkısı bulunmuyor</p>
                  </div>
                )}
              </div>
            )}

            {/* Show message when artist is selected but no tracks available */}
            {selectedArtist && availableTracks.length === 0 && (
              <div className="bg-yellow-500/20 border border-yellow-500/30 rounded-lg p-4">
                <div className="flex">
                  <svg className="w-5 h-5 text-yellow-300 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="text-sm text-yellow-200 font-medium">Şarkı Seçimi Mevcut Değil</p>
                    <p className="text-xs text-yellow-300 mt-1">
                      Bu sanatçının henüz yayınlanmış veya onaylanmış şarkısı bulunmuyor. 
                      Gelir kaydı genel bir ödeme olarak eklenecektir.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Amount and Currency */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-white mb-2">
                  Ödeme Miktarı *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="input-field"
                  value={formData.amount}
                  onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                  placeholder="0.00"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Para Birimi *
                </label>
                <select
                  className="input-field"
                  value={formData.currency}
                  onChange={(e) => setFormData(prev => ({ ...prev, currency: e.target.value }))}
                >
                  <option value="TRY">TRY (₺)</option>
                  <option value="USD">USD ($)</option>
                  <option value="EUR">EUR (€)</option>
                </select>
              </div>
            </div>

            {/* Payment Date */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Ödeme Tarihi
              </label>
              <input
                type="date"
                className="input-field"
                value={formData.payment_date}
                onChange={(e) => setFormData(prev => ({ ...prev, payment_date: e.target.value }))}
              />
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Notlar (İsteğe Bağlı)
              </label>
              <textarea
                rows={3}
                className="input-field resize-none"
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Bu ödeme hakkında ek bilgiler..."
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-8 flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white hover:bg-white/20 transition-colors"
            >
              İptal
            </button>
            <button
              onClick={handleSubmit}
              className="px-6 py-2 bg-gradient-to-r from-green-500 to-green-600 rounded-lg text-white font-medium hover:shadow-lg transition-all"
            >
              Gelir Ekle
            </button>
          </div>
        </div>
      </div>
    );
  };

  const ArtistPaymentModal = ({ artist, onClose }: { artist: Artist, onClose: () => void }) => {
    const artistEarnings = monthlyEarnings.filter(e => e.user_id === artist.user_id);
    const totalAmount = artistEarnings.reduce((sum, e) => sum + parseFloat(e.revenue), 0);
    const payoutInfo = mockPayoutInfo[artist.user_id];
    const [paypalInfo, setPaypalInfo] = useState<PayPalInfo | null>(null);
    const [loadingPaypal, setLoadingPaypal] = useState(true);

    // PayPal bilgilerini yükle
    React.useEffect(() => {
      const loadPayPalInfo = async () => {
        try {
          const info = await getPayPalInfoByUserId(artist.user_id);
          setPaypalInfo(info);
        } catch (error) {
          console.error('PayPal bilgileri yükleme hatası:', error);
        } finally {
          setLoadingPaypal(false);
        }
      };

      loadPayPalInfo();
    }, [artist.user_id]);

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-gray-900 rounded-xl p-6 max-w-2xl w-full mx-4 border border-white/20">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-white">{t('payment.paymentModal')}</h2>
            <button
              onClick={onClose}
              className="text-white/60 hover:text-white"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Sanatçı Bilgileri */}
            <div className="bg-white/5 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-white mb-4">{t('payment.artistInfo')}</h3>
              <div className="space-y-3">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full flex items-center justify-center mr-3">
                    <span className="text-white font-semibold">
                      {artist.display_name.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="text-white font-medium">{artist.display_name}</p>
                    <p className="text-white/60 text-sm">{artist.first_name} {artist.last_name}</p>
                  </div>
                </div>
                <div className="pt-2 border-t border-white/10">
                  <p className="text-sm text-white/70">{t('payment.email')}: {artist.email}</p>
                  <p className="text-sm text-white/70">{t('payment.status')}: {artist.status === 'active' ? t('payment.active') : t('payment.inactive')}</p>
                </div>
              </div>
            </div>

            {/* Ödeme Bilgileri */}
            <div className="bg-white/5 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-white mb-4">{t('payment.accountInfo')}</h3>
              {loadingPaypal ? (
                <div className="flex items-center justify-center py-4">
                  <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin mr-2"></div>
                  <span className="text-sm text-white/70">PayPal bilgileri yükleniyor...</span>
                </div>
              ) : paypalInfo ? (
                <div className="space-y-4">
                  {/* PayPal Bilgileri */}
                  <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                    <div className="flex items-center mb-3">
                      <div className="p-2 bg-blue-500/20 rounded-lg mr-3">
                        <svg className="w-6 h-6 text-blue-300" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h8.418c2.508 0 4.578.087 5.459 1.849.449.899.449 2.448-.449 4.597-.449 1.349-1.347 2.698-2.695 3.598-1.798 1.199-4.058 1.349-6.566 1.349H8.37l-.9 4.297c-.449 2.099-1.798 3.898-4.057 3.898zm6.416-14.335c.9 0 1.348-.45 1.798-1.798.45-1.349.45-2.098-.9-2.098h-2.248l-.9 3.896h2.25z"/>
                        </svg>
                      </div>
                      <div>
                        <p className="text-white font-medium">PayPal Ödeme Bilgileri</p>
                        <p className="text-blue-200 text-xs">Aktif PayPal hesabı mevcut</p>
                      </div>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-white/70">Ad Soyad:</span>
                        <span className="text-white font-medium">{paypalInfo.full_name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/70">PayPal Email:</span>
                        <span className="text-blue-300 font-medium">{paypalInfo.paypal_email}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/70">Son Güncelleme:</span>
                        <span className="text-white/60">{new Date(paypalInfo.updated_at).toLocaleDateString('tr-TR')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/70">Durum:</span>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          paypalInfo.is_verified 
                            ? 'bg-green-500/20 text-green-300' 
                            : 'bg-yellow-500/20 text-yellow-300'
                        }`}>
                          {paypalInfo.is_verified ? 'Doğrulanmış' : 'Doğrulanmamış'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : payoutInfo ? (
                <div className="space-y-3">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-green-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-sm text-white">Eski Ödeme Sistemi (Banka)</span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <p className="text-white/70">{t('payment.paymentType')}: {payoutInfo.type === 'bank' ? t('payment.bank') : t('payment.paypal')}</p>
                    {payoutInfo.type === 'bank' && (
                      <p className="text-white/70">{t('payment.iban')}: {payoutInfo.iban}</p>
                    )}
                    {payoutInfo.type === 'paypal' && (
                      <p className="text-white/70">{t('payment.paypal')}: {payoutInfo.paypal_email}</p>
                    )}
                    <p className="text-white/70">{t('payment.fullName')}: {payoutInfo.full_name}</p>
                    <p className="text-white/70">{t('payment.address')}: {payoutInfo.address}, {payoutInfo.city}</p>
                    <p className="text-white/70">{t('payment.currency')}: {payoutInfo.currency}</p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <svg className="w-8 h-8 text-red-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L5.732 15.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <p className="text-red-300 text-sm">Ödeme bilgisi bulunamadı</p>
                  <p className="text-white/60 text-xs mt-1">
                    Bu sanatçı henüz PayPal bilgilerini kaydetmemiş. Ödeme yapabilmek için sanatçının 
                    profil ayarlarından PayPal bilgilerini eklemesi gerekiyor.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Ödeme Detayları */}
          <div className="mt-6 bg-white/5 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-white mb-4">{t('payment.paymentDetails')}</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-white/70">{t('payment.totalStreams')}:</span>
                <span className="text-white">{artistEarnings.reduce((sum, e) => sum + e.streams, 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/70">{t('payment.totalTracks')}:</span>
                <span className="text-white">{new Set(artistEarnings.map(e => e.track_name)).size}</span>
              </div>
              <div className="flex justify-between border-t border-white/10 pt-3">
                <span className="text-white font-semibold">{t('payment.amountToPay')}:</span>
                <span className="text-green-300 font-bold text-lg">₺{totalAmount.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Ödeme Butonları */}
          <div className="mt-6 flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white hover:bg-white/20 transition-colors"
            >
              {t('payment.cancel')}
            </button>
            <button
              onClick={() => handlePayment(artist.user_id, totalAmount)}
              disabled={!paypalInfo && !payoutInfo}
              className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                (paypalInfo || payoutInfo)
                  ? 'bg-gradient-to-r from-green-500 to-green-600 text-white hover:shadow-lg'
                  : 'bg-gray-500/20 text-gray-400 cursor-not-allowed'
              }`}
            >
              {(paypalInfo || payoutInfo) ? t('payment.makePayment') : 'PayPal Bilgisi Gerekli'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-white">{t('admin.earningsPaymentManagement')}</h1>
          <p className="mt-1 text-sm text-white/70">
            {t('admin.earningsSubtitle')}
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setShowAddEarningModal(true)}
            className="btn-primary flex items-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Gelir Ekle
          </button>
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              {t('admin.selectMonth')}
            </label>
            <select
              className="input-field min-w-40"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
            >
              {availableMonths.map(month => (
                <option key={month} value={month}>
                  {new Date(month + '-01').toLocaleDateString('tr-TR', { year: 'numeric', month: 'long' })}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-xl p-2">
          <nav className="flex space-x-2">
            <button
              onClick={() => setActiveTab('overview')}
              className={`flex-1 py-3 px-4 text-sm font-medium rounded-lg transition-all duration-200 ${
                activeTab === 'overview'
                  ? 'bg-orange-500 text-white shadow-lg'
                  : 'text-white/70 hover:text-white hover:bg-white/10'
              }`}
            >
              {t('admin.overview')}
            </button>
            <button
              onClick={() => setActiveTab('payments')}
              className={`flex-1 py-3 px-4 text-sm font-medium rounded-lg transition-all duration-200 ${
                activeTab === 'payments'
                  ? 'bg-orange-500 text-white shadow-lg'
                  : 'text-white/70 hover:text-white hover:bg-white/10'
              }`}
            >
              {t('admin.paymentManagement')} ({pendingPayments.length})
            </button>
          </nav>
        </div>
      </div>

      {activeTab === 'overview' && (
        <>
          {/* Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="glass-card group hover:scale-105 transition-all duration-300">
              <div className="p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-green-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-white/70 truncate">
                        {t('earnings.totalEarnings')}
                      </dt>
                      <dd className="text-xl font-bold text-white">
                        ₺{totalRevenue.toFixed(2)}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="glass-card group hover:scale-105 transition-all duration-300">
              <div className="p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-white/70 truncate">
                        {t('payment.pendingPayments')}
                      </dt>
                      <dd className="text-xl font-bold text-white">
                        {pendingPayments.length}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="glass-card group hover:scale-105 transition-all duration-300">
              <div className="p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-green-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-white/70 truncate">
                        {t('payment.paidPayments')}
                      </dt>
                      <dd className="text-xl font-bold text-white">
                        {paidPayments.length}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="glass-card group hover:scale-105 transition-all duration-300">
              <div className="p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-white/70 truncate">
                        {t('admin.activeArtists')}
                      </dt>
                      <dd className="text-xl font-bold text-white">
                        {Object.keys(artistBreakdown).length}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            {/* Platform Breakdown */}
            <div className="glass-card">
              <div className="px-6 py-4 border-b border-white/10">
                <h3 className="text-lg font-medium text-white flex items-center">
                  <svg className="w-5 h-5 mr-2 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  {t('admin.platformDistribution')}
                </h3>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {Object.entries(platformBreakdown).map(([platform, revenue]) => (
                    <div key={platform} className="flex items-center justify-between p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors duration-200">
                      <div className="flex items-center">
                        <div className={`w-3 h-3 ${getPlatformColor(platform)} rounded-full mr-3`}></div>
                        <span className="text-sm font-medium text-white">{platform}</span>
                      </div>
                      <span className="text-sm font-semibold text-green-300">₺{revenue.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Artist Breakdown */}
            <div className="glass-card">
              <div className="px-6 py-4 border-b border-white/10">
                <h3 className="text-lg font-medium text-white flex items-center">
                  <svg className="w-5 h-5 mr-2 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  {t('admin.artistDistribution')}
                </h3>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {Object.entries(artistBreakdown).map(([artistName, data]) => (
                    <div key={artistName} className="flex items-center justify-between p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors duration-200">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full mr-3 flex items-center justify-center">
                          <span className="text-xs font-medium text-white">
                            {artistName.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-white">{artistName}</span>
                          <p className="text-xs text-white/60">{data.tracks.size} {t('admin.tracks')}</p>
                        </div>
                      </div>
                      <span className="text-sm font-semibold text-green-300">₺{data.revenue.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {activeTab === 'payments' && (
        <div className="space-y-6">
          {/* Ödeme Bekleyen Sanatçılar */}
          <div className="glass-card">
            <div className="px-6 py-4 border-b border-white/10">
              <h3 className="text-lg font-medium text-white flex items-center">
                <svg className="w-5 h-5 mr-2 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {t('admin.pendingPaymentsArtists')}
              </h3>
            </div>
            <div className="p-6">
              {Object.entries(artistBreakdown).map(([artistName, data]) => {
                const artist = data.artist;
                if (!artist) return null;
                
                const artistPendingEarnings = monthlyEarnings.filter(
                  e => e.user_id === artist.user_id && e.payment_status === 'pending'
                );
                
                if (artistPendingEarnings.length === 0) return null;
                
                const pendingAmount = artistPendingEarnings.reduce((sum, e) => sum + parseFloat(e.revenue), 0);
                const hasPayoutInfo = mockPayoutInfo[artist.user_id];

                return (
                  <div key={artist.user_id} className="flex items-center justify-between p-4 bg-white/5 rounded-lg hover:bg-white/10 transition-colors duration-200 mb-4">
                    <div className="flex items-center flex-1">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full mr-4 flex items-center justify-center">
                        <span className="text-white font-semibold">
                          {artist.display_name.charAt(0)}
                        </span>
                      </div>
                      <div className="flex-1">
                        <h4 className="text-white font-medium">{artist.display_name}</h4>
                        <p className="text-white/60 text-sm">{artist.first_name} {artist.last_name}</p>
                        <div className="flex items-center mt-1">
                          <span className="text-green-300 font-semibold">₺{pendingAmount.toFixed(2)}</span>
                          <span className="text-white/60 text-sm ml-2">
                            • {artistPendingEarnings.reduce((sum, e) => sum + e.streams, 0).toLocaleString()} streams
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {hasPayoutInfo ? (
                          <div className="flex items-center text-green-400">
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="text-sm">{t('payment.accountInfoAvailable')}</span>
                          </div>
                        ) : (
                          <div className="flex items-center text-red-400">
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L5.732 15.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                            <span className="text-sm">{t('payment.accountInfoRequired')}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="ml-4">
                      <button
                        onClick={() => setSelectedArtist(artist)}
                        className="px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg text-white font-medium hover:shadow-lg transition-all duration-200"
                      >
                        {t('payment.makePayment')}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Ödeme Geçmişi */}
          <div className="glass-card">
            <div className="px-6 py-4 border-b border-white/10">
              <h3 className="text-lg font-medium text-white flex items-center">
                <svg className="w-5 h-5 mr-2 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {t('admin.paymentHistory')}
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-white/10">
                <thead className="bg-white/5">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">
                      {t('admin.artist')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">
                      {t('admin.song')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">
                      {t('admin.platform')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">
                      {t('payment.paymentAmount')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">
                      {t('payment.paymentStatus')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">
                      {t('payment.paymentDate')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white/5 divide-y divide-white/10">
                  {monthlyEarnings.map((earning, index) => {
                    const artist = artists.find(a => a.user_id === earning.user_id);
                    return (
                      <tr key={index} className="hover:bg-white/10 transition-colors duration-200">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full flex items-center justify-center mr-3">
                              <span className="text-xs font-medium text-white">
                                {(artist?.display_name || 'Unknown').charAt(0)}
                              </span>
                            </div>
                            <div>
                              <div className="text-sm font-medium text-white">
                                {artist?.display_name || 'Unknown'}
                              </div>
                              <div className="text-sm text-white/60">
                                {artist?.first_name} {artist?.last_name}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-white">{earning.track_name}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-white/10 text-white">
                            {earning.platform}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-semibold text-green-300">
                            ₺{parseFloat(earning.revenue).toFixed(2)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getPaymentStatusColor(earning.payment_status)}`}>
                            {getPaymentStatusText(earning.payment_status)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-white/70">
                          {earning.payment_date ? new Date(earning.payment_date).toLocaleDateString('tr-TR') : '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Add Earning Modal */}
      {showAddEarningModal && (
        <AddEarningModal
          onClose={() => setShowAddEarningModal(false)}
        />
      )}

      {/* Payment Modal */}
      {selectedArtist && (
        <ArtistPaymentModal
          artist={selectedArtist}
          onClose={() => setSelectedArtist(null)}
        />
      )}
      
      {/* Toast Messages */}
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={() => hideToast(toast.id)}
        />
      ))}
    </div>
  );
};

export default AdminEarnings; 